import PropTypes from 'prop-types';
import {SearchBox} from '../SearchBox';
import SearchForm from '../SearchForm';

import React, {Component} from 'react';
import {constructSearchRequest, generateSearchPageLink} from '../../utils/search';
import {countOfNights} from '../../utils/dates';
import {getSearchValues, removeDatesFromTripCookie, removeDatesFromSearchCookie} from '../../utils/cookies';

import './style.less';

export function isSearchDataDifferent(nextProps, props) {
    return (
        nextProps.checkIn !== props.checkIn ||
        nextProps.checkOut !== props.checkOut ||
        nextProps.guests !== props.guests
    );
}

export default class Search extends Component {
    static propTypes = {
        /** Destination label */
        destination: PropTypes.string,
        /** Location Based Search ID */
        lbsId: PropTypes.string.isRequired,
        /** Selected check-in date */
        checkIn: PropTypes.string,
        /** Selected check-out date */
        checkOut: PropTypes.string,
        /** Guests information */
        guests: PropTypes.shape({
            adultsCount: PropTypes.number,
            childrenCount: PropTypes.number,
            petIncluded: PropTypes.bool
        }),
        /** Max Number of Guests */
        maxNumberOfGuests: PropTypes.number,
        /** Get the search sticked to the top on scroll */
        stickScrollTop: PropTypes.number,
        /** Function to call when search form is submitted */
        onSubmit: PropTypes.func.isRequired,
        /** Function to call when component updates */
        onUpdate: PropTypes.func,
        /** Labels to display */
        labels: PropTypes.shape({
            buttonTitle: PropTypes.string,
            datePicker: PropTypes.shape({}),
            guests: PropTypes.shape({})
        }),
        /** Allow overriding DayAvailability component externally */
        dayTemplateComponent: PropTypes.func,
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        /** Activates mobile mode for searchbox */
        isMobile: PropTypes.bool,
        /** Additional props for Typeahead */
        typeaheadProps: PropTypes.shape({}),
        /** Edap event name */
        edapEventName: PropTypes.shape({}),
        /** Edap action location */
        edapActionlocation: PropTypes.string,
        /** Edap action location for search_query.submitted */
        edapActionlocationSearchQuerySubmitted: PropTypes.string,
        /** Edap action location for guest_update.submitted */
        edapActionlocationGuestUpdateSubmitted: PropTypes.string,
        /** Domain */
        domain: PropTypes.string,
        /** Property Type */
        propertyType: PropTypes.string,
        /** Split date pickers */
        splitDatePicker: PropTypes.bool,
        /** filter id to be applied in SeRP URL constructing */
        fabId: PropTypes.number,
        /** A callback to be called on date pickers opened  */
        onOpen: PropTypes.func
    };

    static defaultProps = {
        destination: null,
        lbsId: null,
        guests: {
            adultsCount: null,
            childrenCount: null,
            petIncluded: null
        },
        maxNumberOfGuests: 10,
        onUpdate: () => {},
        locale: 'en_US',
        labels: {
            buttonTitle: 'Search',
            datePicker: {
                checkIn: 'Check In',
                checkOut: 'Check Out',
                labels: 'Clear',
                close: 'Close',
                done: 'Done',
                arrival: 'Select your arrival date',
                departure: 'Select your departure date'
            },
            guests: {
                label: 'Guests',
                adult: 'Adult',
                adults: 'Adults',
                child: 'Child',
                children: 'Children',
                pets: 'pets',
                guests: 'guests',
                yesBtn: 'Yes',
                noBtn: 'No',
                applyBtn: 'Apply',
                modalTitle: 'Select guest ',
                mobileBtn: 'Continue'
            },
            typeahead: {
                destinationplaceholdermsg: 'Where to?'
            }
        },
        isMobile: false,
        edapEventName: {
            name: 'search.submit',
            datedName: 'dated.search.submit',
            searchQuerySubmitted: 'search_query.submitted'
        },
        edapActionlocation: 'search-widget',
        edapActionlocationSearchQuerySubmitted: '',
        domain: 'www.homeaway.com',
        splitDatePicker: false
    };

    constructor(props) {
        super(props);

        this.state = {
            guests: props.guests,
            lbsId: props.lbsId,
            listingProps: null,
            isLoadingSerp: false,
            isTypeaheadInputFocus: false,
            destination: props.destination,
            checkIn: props.checkIn,
            checkOut: props.checkOut,
            isDatePickerOpened: false
        };
    }

    componentDidMount() {
        window.edap = window.edap || [];
        window.addEventListener('pageshow', this.handlePersistedState, false);

        const searchCookies = getSearchValues();

        this.setState({ // eslint-disable-line react/no-did-mount-set-state
            checkIn: this.props.checkIn ? this.props.checkIn : searchCookies.arrivalDate,
            checkOut: this.props.checkOut ? this.props.checkOut : searchCookies.departureDate,
            guests: this.getGuests(searchCookies),
            destination: this.props.destination ? this.props.destination : searchCookies.destination
        });
    }

    componentWillReceiveProps(nextProps) {
        const isNewSearchData = nextProps.checkIn || nextProps.checkOut;
        if (isNewSearchData && isSearchDataDifferent(nextProps, this.props)) {
            this.setState({
                checkIn: nextProps.checkIn,
                checkOut: nextProps.checkOut,
                guests: nextProps.guests
            });
        }
    }

    componentWillUpdate(nextProps, nextState) {
        if (this.props.onUpdate) {
            this.props.onUpdate(nextState);
        }
    }

    getGuests(guests) {
        return {
            adultsCount: this.props.guests.adultsCount !== null ? this.props.guests.adultsCount : guests.adults,
            childrenCount: this.props.guests.childrenCount !== null ? this.props.guests.childrenCount : guests.children,
            petIncluded: this.props.guests.petIncluded !== null ? this.props.guests.petIncluded : guests.petIncluded
        };
    }

    handlePersistedState = () => {
        this.setState({isLoadingSerp: false});
    }

    onInputQuery = (query) => {
        this.setState({
            destination: query.destination,
            subDestination: query.subDestination || {},
            lbsId: query.destinationUuid,
            listingProps: query.listingProps || null
        });
    };

    onDatePickerClose = () => {
        const {checkIn, checkOut} = this.state;
        const obj = {
            isDatePickerOpened: false
        };

        if (!checkIn || !checkOut) {
            obj.checkIn = '';
            obj.checkOut = '';
        }

        this.setState(obj);
    }

    onInputCheckIn = (value) => {
        this.setState({checkIn: value});
    };

    onInputCheckOut = (value) => {
        this.setState((prevState) => ({
            checkOut: value,
            isDatePickerOpened: this.props.isMobile ? true : !(prevState.checkIn && value),
            isGuestPickerOpened: false
        }));
    };

    onInputGuest = (key, value) => {
        if (key === 'clear') {
            this.setState({guests: value});
        } else {
            this.setState({guests: {...this.state.guests, [key]: value}});
        }
    };

    onInputGuestClose = () => {
        this.setState({
            isGuestPickerOpened: false,
            isDatePickerOpened: false
        });
    };

    onInputGuestExit = (undoState) => {
        this.setState({guests: undoState});
    }

    onSubmitButtonClicked = (e) => {
        e.preventDefault();
        this.onSubmit();
    }

    getRefinements(event) {
        const {guests, checkIn, checkOut, fabId} = event;
        const adults = guests.adultsCount || 0;
        const children = guests.childrenCount || 0;
        const pets = guests.petIncluded || false;
        const edapRefinements = [
            `adults_${adults}`,
            `children_${children}`,
            `sleeps_${adults + children}`,
            `pets_${pets}`
        ];

        if (checkIn && checkOut) {
            edapRefinements.push(`nights_${countOfNights(checkIn, checkOut)}`);
            const currenDate = new Date();
            edapRefinements.push(`window_${countOfNights(currenDate, checkIn)}`);
        }

        if (fabId) {
            edapRefinements.push(`filter:${fabId}`);
        }

        return edapRefinements.join(',');
    }

    getEdapData(event) {
        return {
            actionlocation: this.props.edapActionlocation,
            eventrefinements: this.getRefinements(event),
            numadults: event.guests.adultsCount,
            withpets: event.guests.petIncluded,
            numchildren: event.guests.childrenCount
        };
    }

    pushEdapEvent(event) {
        const {checkIn, checkOut} = event;
        const {
            edapActionlocationSearchQuerySubmitted,
            edapEventName: {searchQuerySubmitted}
        } = this.props;

        const adls = this.getEdapData(event);
        const hasDates = checkIn && checkOut;
        const edapEventName = hasDates ? this.props.edapEventName.datedName : this.props.edapEventName.name;

        if (hasDates) {
            adls.startdate = checkIn;
            adls.enddate = checkOut;
            adls.lengthofstay = countOfNights(checkIn, checkOut);
        }

        window.edap.push((edap) => edap.trigger(edapEventName, adls, {delayProcessing: true}));
        window.edap.push((edap) => edap.trigger(searchQuerySubmitted, {action_location: edapActionlocationSearchQuerySubmitted}));
    }

    onTypeaheadBlur = () => {
        this.setState({
            isTypeaheadInputFocus: false,
            isGuestPickerOpened: false
        });
    }

    generateListingPageLink = (listingUrl, unitId) => {
        let searchParams = {};
        const {checkIn, checkOut, guests: {adultsCount, childrenCount, petIncluded}} = this.state;
        const isCheckInSet = checkIn && checkIn !== '';
        const isCheckOutSet = checkOut && checkOut !== '';
        const areDatesFilled = isCheckInSet && isCheckOutSet;
        searchParams = {
            ...searchParams,
            ...(areDatesFilled && {arrival: checkIn}),
            ...(areDatesFilled && {departure: checkOut}),
            ...(!areDatesFilled && {noDates: true}),
            ...(adultsCount && {adultsCount}),
            ...(childrenCount && {childrenCount}),
            ...(petIncluded && {petIncluded})
        };
        const connector = unitId ? '&' : '?';
        const searchPath = `${listingUrl}${connector}${Object.entries(searchParams).map(([key, value]) => `${key}=${value}`).join('&')}`;
        return searchPath;
    }

    onSubmit = () => {
        if (!this.state.destination) {
            this.setState({isTypeaheadInputFocus: true});
            return;
        }

        if (!this.state.checkIn && !this.state.checkOut) {
            removeDatesFromTripCookie();
            removeDatesFromSearchCookie();
        }

        this.setState({isLoadingSerp: true}, () => {
            const event = {
                checkIn: this.state.checkIn,
                checkOut: this.state.checkOut,
                guests: this.state.guests,
                destination: this.state.destination,
                subDestination: this.state.subDestination,
                lbsId: this.state.lbsId,
                fabId: this.props.fabId
            };

            this.pushEdapEvent(event);

            const searchRequest = constructSearchRequest(event);
            let searchPath = generateSearchPageLink(
                searchRequest,
                {propertyType: this.props.propertyType}
            );
            const {listingProps} = this.state;
            if (listingProps && listingProps.listingUrl) {
                const {listingUrl, unitId = null} = listingProps;
                searchPath = this.generateListingPageLink(listingUrl, unitId);
            }

            this.props.onSubmit({...event, searchPath});
        });
    }

    render() {
        const {destination, guests, checkIn, checkOut, isLoadingSerp, isGuestPickerOpened, isDatePickerOpened, isTypeaheadInputFocus} = this.state;
        const {labels, dayTemplateComponent, locale, typeaheadProps, splitDatePicker, maxNumberOfGuests, isMobile, onOpen, edapActionlocationGuestUpdateSubmitted} = this.props;

        return (
            <div className="search__container">
                <SearchBox>
                    <SearchForm
                        query={destination}
                        maxNumGuests={maxNumberOfGuests}
                        onSubmit={this.onSubmitButtonClicked}
                        onBlur={this.onTypeaheadBlur}
                        onInputQuery={this.onInputQuery}
                        onInputGuest={this.onInputGuest}
                        onInputGuestClose={this.onInputGuestClose}
                        onInputGuestExit={this.onInputGuestExit}
                        onInputCheckIn={this.onInputCheckIn}
                        onUpdate
                        onInputCheckOut={this.onInputCheckOut}
                        onClose={this.onDatePickerClose}
                        {...{labels, locale, guests, checkIn, checkOut, typeaheadProps, isLoadingSerp,
                            isGuestPickerOpened, isDatePickerOpened, isTypeaheadInputFocus, isMobile, splitDatePicker, dayTemplateComponent, onOpen,
                            edapActionlocationGuestUpdateSubmitted}}
                    />
                </SearchBox>
            </div>
        );
    }
}
